/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 01/03/2026 | Lic. José Luis Aragón Cervantes  | Ajustes en lógica de cobranza y validación de total efectivo y cambio
*      3       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Agregar ayuda visual de monto faltante en cobro
*      4       | 16/06/2026 | Lic. José Luis Aragón Cervantes  | Refactor useReducer: rendimiento en escritura, elimina useMemo/useCallback innecesarios
*      5       | 16/08/2026 | Lic. José Luis Aragón Cervantes  | Integración de pago con vales en Venta Super
*      6       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Abono inicial separado para crédito a cliente
*      7       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Redondeo de liquidación a cuatro decimales
*      8       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Resto de abono inicial calculado por forma de pago
*      9       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Diseño compacto del resumen de cobro
*      10      | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Abono inicial calculado por forma de pago y efectivo
* --------------------------------------------------------------------------------------- 
*/
import TextField from "../ui/TextField";
import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  AttachMoney,
  CreditCard,
  AccountBalance,
  AccountBalanceWallet,
} from "@mui/icons-material";
import { Modal, Box, MenuItem, Typography, Alert, Button, Checkbox, FormControlLabel } from "@mui/material";
import { NumberFormat$ } from "../../settings/utils";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useRequest } from "../../context/System/RequestContext";
import { apiObtenerValePorCambio } from "../../settings/ApiConfig";
const PAYMENT_TYPES = [
  "EFECTIVO",
  "TRANSFERENCIA",
  "TARJETA DÉBITO",
  "TARJETA CRÉDITO",
  "VALES",
  "CRÉDITO A CLIENTE",
];
const modalStyle = {
  position: "fixed",
  top: "2vh",
  right: "27%",
  width: "49vw",
  maxWidth: "82vw",
  maxHeight: "96vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 1.25,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  transform: "translateX(300%)",
  transition: "transform 0.35s ease-in-out",
  overflowY: "auto",
  overflowX: "hidden",
};
const DECIMALES_LIQUIDACION = 4;
const FACTOR_LIQUIDACION = 10 ** DECIMALES_LIQUIDACION;
const roundLiquidacion = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * FACTOR_LIQUIDACION) / FACTOR_LIQUIDACION;
};
const formatLiquidacion = (value) => roundLiquidacion(value).toFixed(DECIMALES_LIQUIDACION);
const getSafeSummary = (montosVenta) => ({
  subTotal: roundLiquidacion(montosVenta?.subTotal),
  totalDescuento: roundLiquidacion(montosVenta?.totalDescuento),
  totalIVA: roundLiquidacion(montosVenta?.totalIVA),
  total: roundLiquidacion(montosVenta?.total),
  montoEnLetra: montosVenta?.montoEnLetra || "",
});
const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/,/g, "").trim();
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
};
const onlyDigits = (value, maxLength = 4) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxLength);
const getMontoValePorCambio = (state) =>
  state.aplicaValePorCambio ? roundLiquidacion(parseAmount(state.montoValePorCambio)) : 0;
const getTotalACubrir = (summary, state) =>
  roundLiquidacion(Math.max(roundLiquidacion(summary?.total) - getMontoValePorCambio(state), 0));
const initialState = {
  tipoPago: "EFECTIVO",
  montoEfectivo: "",
  montoOtro: "",
  digitosTarjeta: "",
  datosTransferencia: "",
  datosCreditoCliente: "",
  formaPagoAbonoInicial: "EFECTIVO",
  datosVales: "",
  aplicaValePorCambio: false,
  folioValePorCambio: "",
  remisionDevolucion: "",
  montoValePorCambio: "",
  montoValePorCambioNum: 0,
  montoEfectivoNum: 0,
  montoOtroNum: 0,
  totalIngresado: 0,
  faltante: 0,
  cambio: 0,
  montoCambioLetra: "CERO PESOS",
};
function cobroReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      const next = { ...state, [action.field]: action.value };
      if (action.field === 'montoEfectivo') next.montoEfectivo = String(action.value);
      if (action.field === 'montoOtro') next.montoOtro = String(action.value);
      if (action.field === 'montoValePorCambio') next.montoValePorCambio = String(action.value);
      if (action.field === 'aplicaValePorCambio' && !action.value) {
        next.folioValePorCambio = "";
        next.remisionDevolucion = "";
        next.montoValePorCambio = "";
      }
      const montoValePorCambioNum = getMontoValePorCambio(next);
      const total = roundLiquidacion(Math.max(roundLiquidacion(action.summaryTotal) - montoValePorCambioNum, 0));
      const esCreditoCliente = next.tipoPago === "CRÉDITO A CLIENTE";
      const montoOtroCapturado = roundLiquidacion(parseAmount(next.montoOtro));
      const recalculaEfectivo = !esCreditoCliente && (action.field === 'montoOtro'
        || action.field === 'montoValePorCambio'
        || action.field === 'aplicaValePorCambio');
      const montoEfectivoNum = roundLiquidacion(recalculaEfectivo
        ? Math.max(total - montoOtroCapturado, 0)
        : parseAmount(next.montoEfectivo));
      const montoOtroNum = roundLiquidacion(esCreditoCliente
        ? (next.formaPagoAbonoInicial === "EFECTIVO" ? 0 : montoOtroCapturado)
        : montoOtroCapturado);
      const totalIngresado = roundLiquidacion(esCreditoCliente ? total : montoEfectivoNum + montoOtroNum);
      const cambio = roundLiquidacion(esCreditoCliente ? 0 : Math.max(totalIngresado - total, 0));
      const faltante = roundLiquidacion(esCreditoCliente ? 0 : Math.max(total - totalIngresado, 0));
      return {
        ...next,
        montoEfectivo: recalculaEfectivo ? String(montoEfectivoNum) : next.montoEfectivo,
        montoOtro: esCreditoCliente && next.formaPagoAbonoInicial === "EFECTIVO"
          ? formatLiquidacion(0)
          : next.montoOtro,
        montoEfectivoNum,
        montoOtroNum,
        montoValePorCambioNum,
        totalIngresado,
        faltante,
        cambio,
      };
    }
    case 'SET_DIGITOS_TARJETA':
      return { ...state, digitosTarjeta: onlyDigits(action.value, 4) };
    case 'CHANGE_PAYMENT_TYPE': {
      const montoValePorCambioNum = getMontoValePorCambio(state);
      const total = roundLiquidacion(Math.max(roundLiquidacion(action.total) - montoValePorCambioNum, 0));
      const valeState = {
        aplicaValePorCambio: state.aplicaValePorCambio,
        folioValePorCambio: state.folioValePorCambio,
        remisionDevolucion: state.remisionDevolucion,
        montoValePorCambio: state.montoValePorCambio,
        montoValePorCambioNum,
      };
      if (action.tipoPago === "EFECTIVO" && total > 0) {
        const montoEfectivoStr = String(total);
        return {
          ...initialState,
          ...valeState,
          tipoPago: "EFECTIVO",
          montoEfectivo: montoEfectivoStr,
          montoEfectivoNum: total,
          totalIngresado: total,
          faltante: 0,
          cambio: 0,
          montoCambioLetra: state.montoCambioLetra,
        };
      }
      if (action.tipoPago === "CRÉDITO A CLIENTE") {
        return {
          ...initialState,
          ...valeState,
          tipoPago: "CRÉDITO A CLIENTE",
          montoEfectivo: formatLiquidacion(0),
          montoEfectivoNum: 0,
          montoOtro: formatLiquidacion(0),
          montoOtroNum: 0,
          totalIngresado: total,
          faltante: 0,
          cambio: 0,
          montoCambioLetra: state.montoCambioLetra,
        };
      }
      return { ...initialState, ...valeState, tipoPago: action.tipoPago, montoCambioLetra: state.montoCambioLetra, faltante: total };
    }
    case 'SET_CAMBIO_LETRA':
      return { ...state, montoCambioLetra: action.value };
    case 'RESET': {
      const total = roundLiquidacion(action.total);
      const montoEfectivoStr = total > 0 ? String(total) : "";
      return {
        ...initialState,
        tipoPago: action.tipoPago || "EFECTIVO",
        montoEfectivo: montoEfectivoStr,
        montoEfectivoNum: total,
        totalIngresado: total,
        faltante: 0,
        cambio: 0,
      };
    }
    default:
      return state;
  }
}
function validateBeforeCharge(state, summary) {
  if (summary.total <= 0) return "No hay total de venta valido para cobrar.";
  const montoValePorCambio = getMontoValePorCambio(state);
  const totalACubrir = getTotalACubrir(summary, state);
  if (state.aplicaValePorCambio) {
    if (parseAmount(state.folioValePorCambio) <= 0) return "Ingrese el folio del vale por cambio.";
    if (parseAmount(state.remisionDevolucion) <= 0) return "Ingrese la remision origen del vale por cambio.";
    if (montoValePorCambio <= 0) return "Ingrese el monto del vale por cambio.";
    // El vale puede ser mayor al total: el excedente se pierde y el vale se finiquita (nunca negativos).
  }
  if (state.tipoPago === "EFECTIVO") {
    if (totalACubrir > 0 && state.montoEfectivoNum <= 0) return "Ingrese el monto en efectivo.";
    if (state.montoEfectivoNum < totalACubrir) return "El efectivo es insuficiente para cubrir la venta.";
    return "";
  }
  if (state.tipoPago !== "CRÉDITO A CLIENTE") {
    if (totalACubrir > 0 && state.montoOtroNum <= 0) return "Ingrese el monto del tipo de pago.";
    if (state.totalIngresado < totalACubrir) return "La suma de montos no cubre el total de la venta.";
  }
  const isCard = state.tipoPago === "TARJETA D\u00c9BITO" || state.tipoPago === "TARJETA CR\u00c9DITO";
  const esTarjetaAbonoInicial = state.tipoPago === "CR\u00c9DITO A CLIENTE"
    && (state.formaPagoAbonoInicial === "TARJETA DÉBITO" || state.formaPagoAbonoInicial === "TARJETA CRÉDITO");
  if ((isCard || esTarjetaAbonoInicial) && state.digitosTarjeta.trim() !== "" && state.digitosTarjeta.length !== 4)
    return "Ingrese los 4 digitos de la tarjeta.";
  if (state.tipoPago === "TRANSFERENCIA" && state.datosTransferencia.trim() !== "" && !state.datosTransferencia.trim())
    return "Ingrese los datos de la transferencia.";
  if (state.tipoPago === "CR\u00c9DITO A CLIENTE" && state.datosCreditoCliente.trim() !== "" && !state.datosCreditoCliente.trim())
    return "Ingrese los datos del credito a cliente.";
  if (state.tipoPago === "VALES" && !state.datosVales.trim())
    return "Ingrese el folio o referencia del vale.";
  return "";
}
function buildPayload(state, summary) {
  const isCard = state.tipoPago === "TARJETA DÉBITO" || state.tipoPago === "TARJETA CRÉDITO"
    || (state.tipoPago === "CRÉDITO A CLIENTE" && (state.formaPagoAbonoInicial === "TARJETA DÉBITO" || state.formaPagoAbonoInicial === "TARJETA CRÉDITO"));
  const isTransferencia = state.tipoPago === "TRANSFERENCIA";
  const isCredito = state.tipoPago === "CRÉDITO A CLIENTE";
  const isVales = state.tipoPago === "VALES";
  const formaPagoAbonoInicial = isCredito ? state.formaPagoAbonoInicial : "";
  const abonoInicialConTarjeta = formaPagoAbonoInicial === "TARJETA DÉBITO" || formaPagoAbonoInicial === "TARJETA CRÉDITO";
  const montoTipoPagoAbonoInicial = isCredito && formaPagoAbonoInicial !== "EFECTIVO"
    ? roundLiquidacion(state.montoOtroNum)
    : 0;
  const efectivoAbonoInicial = isCredito ? roundLiquidacion(state.montoEfectivoNum) : 0;
  const montoAbonoInicial = isCredito
    ? roundLiquidacion(efectivoAbonoInicial + montoTipoPagoAbonoInicial)
    : 0;
  const montoCreditoCliente = isCredito
    ? roundLiquidacion(Math.max(getTotalACubrir(summary, state) - montoAbonoInicial, 0))
    : state.montoOtroNum;
  const referenciaAbonoInicial = formaPagoAbonoInicial === "TRANSFERENCIA"
    ? state.datosTransferencia.trim()
    : formaPagoAbonoInicial === "VALES"
      ? state.datosVales.trim()
      : abonoInicialConTarjeta
        ? state.digitosTarjeta.trim()
        : "";
  // El abono inicial de un crédito se conserva sólo en Abonos, nunca en C_Remision.
  const efectivoEnRemision = isCredito ? 0 : state.montoEfectivoNum;
  const referencia = isCredito
    ? state.datosCreditoCliente.trim()
    : isTransferencia
      ? state.datosTransferencia.trim()
      : isVales
        ? state.datosVales.trim()
        : isCard
          ? state.digitosTarjeta.trim()
          : "";
  return {
    foliocortecaja: 0,
    remision: 0,
    pc: "",
    usuario: "",
    clavecliente: "",
    razonsocial: "",
    fechaventa: "",
    horaventa: "",
    factura: "0",
    facturaelectronica: "",
    condicionesdepago: "",
    tipopago: state.tipoPago.trim(),
    serialtarjetachequevale: referencia,
    totaltipopago: roundLiquidacion(montoCreditoCliente),
    totalefectivo: roundLiquidacion(efectivoEnRemision),
    totaldescuento: roundLiquidacion(summary.totalDescuento),
    totalcambio: isCredito ? 0 : roundLiquidacion(state.cambio),
    numeroautorizacion: isCard && !isCredito ? state.digitosTarjeta.trim() : "",
    tipodebitocredito: state.tipoPago,
    banco: "",
    subtotal: roundLiquidacion(summary.subTotal),
    iva: roundLiquidacion(summary.totalIVA),
    total: roundLiquidacion(summary.total),
    totalenletra: summary.montoEnLetra,
    foliovaleporcambio: state.aplicaValePorCambio ? parseAmount(state.folioValePorCambio) : 0,
    remisiondevolucion: state.aplicaValePorCambio ? parseAmount(state.remisionDevolucion) : 0,
    montovaleporcambio: state.aplicaValePorCambio ? getMontoValePorCambio(state) : 0,
    montoabonoinicial: montoAbonoInicial,
    efectivoabonoinicial: efectivoAbonoInicial,
    formapagoabonoinicial: formaPagoAbonoInicial,
    referenciaabonoinicial: isCredito ? referenciaAbonoInicial : "",
    observacionabonoinicial: isCredito ? "Abono inicial generado desde Venta Súper" : "",
  };
}
export default function ModalCobroVentas(props) {
  const {
    openModalCobrarVentas,
    montosVenta,
    handleCloseModalCobroVentas,
    handleCobrar,
    tieneCliente = false,
  } = props;
  const { MontoEnLetra } = useVentas();
  const { GetRequest } = useRequest();
  const [internalOpen, setInternalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [state, dispatch] = useReducer(cobroReducer, initialState);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [valeMensaje, setValeMensaje] = useState(null); // { tipo: 'success'|'warning'|'error'|'info', texto: '' }
  const [consultandoVale, setConsultandoVale] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const logRef = useRef([]);
  const mountedRef = useRef(false);
  const prevCobroOpenRef = useRef(false);
  const refTipoPago = useRef(null);
  const refMontoEfectivo = useRef(null);
  const refMontoOtro = useRef(null);
  const refDigitos = useRef(null);
  const refTransferencia = useRef(null);
  const refCreditoCliente = useRef(null);
  const refVales = useRef(null);
  const refFolioValePorCambio = useRef(null);
  const summary = useMemo(() => getSafeSummary(montosVenta), [montosVenta]);
  const totalACubrir = useMemo(() => getTotalACubrir(summary, state), [summary, state.aplicaValePorCambio, state.montoValePorCambio]);
  const isCredito = state.tipoPago === "CRÉDITO A CLIENTE";
  const isCardPayment = state.tipoPago === "TARJETA DÉBITO" || state.tipoPago === "TARJETA CRÉDITO";
  const requiresMontoOtro = state.tipoPago !== "EFECTIVO";
  const isTarjetaAbonoInicial = isCredito && (state.formaPagoAbonoInicial === "TARJETA DÉBITO" || state.formaPagoAbonoInicial === "TARJETA CRÉDITO");
  const isTransferenciaAbonoInicial = isCredito && state.formaPagoAbonoInicial === "TRANSFERENCIA";
  const isValeAbonoInicial = isCredito && state.formaPagoAbonoInicial === "VALES";
  const requiresReferenciaTarjeta = isCardPayment || isTarjetaAbonoInicial;
  const requiresReferenciaTransferencia = state.tipoPago === "TRANSFERENCIA" || isTransferenciaAbonoInicial;
  const requiresReferenciaCredito = state.tipoPago === "CRÉDITO A CLIENTE";
  const requiresReferenciaVales = state.tipoPago === "VALES" || isValeAbonoInicial;
  const mostrarMontoTipoPagoAbonoInicial = isCredito && state.formaPagoAbonoInicial !== "EFECTIVO";
  const montoTipoPagoAbonoInicial = mostrarMontoTipoPagoAbonoInicial
    ? roundLiquidacion(state.montoOtroNum)
    : 0;
  const efectivoAbonoInicial = isCredito ? roundLiquidacion(state.montoEfectivoNum) : 0;
  const montoAbonoInicial = isCredito
    ? roundLiquidacion(efectivoAbonoInicial + montoTipoPagoAbonoInicial)
    : 0;
  const montoCreditoCliente = roundLiquidacion(Math.max(totalACubrir - montoAbonoInicial, 0));
  const canCharge = !isProcessing && summary.total > 0 && state.faltante <= 0 && (
    totalACubrir === 0
      ? true
      : state.tipoPago === "EFECTIVO"
        ? state.montoEfectivoNum >= totalACubrir && state.montoEfectivoNum > 0
        : state.totalIngresado >= totalACubrir
  );
  const canChargeRef = useRef(false);
  canChargeRef.current = canCharge;
  const isProcessingRef = useRef(false);
  isProcessingRef.current = isProcessing;
  const writeInternalLog = useCallback((level, message, extra = null) => {
    const entry = { time: new Date().toISOString(), level, message, extra };
    logRef.current.push(entry);
    if (logRef.current.length > 300) logRef.current = logRef.current.slice(-300);
    if (typeof window !== "undefined") window.__modalCobroVentasLog = logRef.current;
    if (level === "error") console.error("[ModalCobroVentas]", message, extra ?? "");
  }, []);
  const focusInputByPaymentType = useCallback((tipoPago) => {
    setTimeout(() => {
      if (tipoPago === "EFECTIVO") {
        refMontoEfectivo.current?.focus?.();
        refMontoEfectivo.current?.select?.();
        return;
      }
      if (tipoPago === "TRANSFERENCIA") {
        refTransferencia.current?.focus?.();
        return;
      }
      if (tipoPago === "CRÉDITO A CLIENTE") {
        refCreditoCliente.current?.focus?.();
        return;
      }
      if (tipoPago === "VALES") {
        refVales.current?.focus?.();
        return;
      }
      if (tipoPago === "TARJETA DÉBITO" || tipoPago === "TARJETA CRÉDITO") {
        refMontoOtro.current?.focus?.();
        refMontoOtro.current?.select?.();
      }
    }, 120);
  }, []);
  const handleInputChange = (field, value) => {
    dispatch({ type: 'SET_FIELD', field, value, summaryTotal: summary.total });
  };
  const focusFolioVale = useCallback(() => {
    setTimeout(() => {
      refFolioValePorCambio.current?.focus?.();
      refFolioValePorCambio.current?.select?.();
    }, 120);
  }, []);
  const handleToggleAplicaVale = useCallback((checked) => {
    dispatch({ type: 'SET_FIELD', field: 'aplicaValePorCambio', value: checked, summaryTotal: summaryRef.current?.total ?? 0 });
    setValeMensaje(null);
    if (checked) focusFolioVale();
  }, [focusFolioVale]);
  const handleFolioValeChange = useCallback((value) => {
    const folioSoloDigitos = String(value ?? "").replace(/\D/g, "").slice(0, 12);
    const totalActual = summaryRef.current?.total ?? 0;
    dispatch({ type: 'SET_FIELD', field: 'folioValePorCambio', value: folioSoloDigitos, summaryTotal: totalActual });
    dispatch({ type: 'SET_FIELD', field: 'remisionDevolucion', value: "", summaryTotal: totalActual });
    dispatch({ type: 'SET_FIELD', field: 'montoValePorCambio', value: "", summaryTotal: totalActual });
    setValeMensaje(null);
  }, []);
  const consultarValePorFolio = useCallback(async (folioRaw) => {
    const folio = String(folioRaw ?? "").replace(/\D/g, "").trim();
    if (!folio || parseAmount(folio) <= 0) {
      setValeMensaje({ tipo: 'warning', texto: 'Escanee o capture el folio numérico del vale y presione Enter.' });
      focusFolioVale();
      return;
    }
    setConsultandoVale(true);
    setValeMensaje({ tipo: 'info', texto: `Consultando vale ${folio}...` });
    try {
      const response = await GetRequest({
        url: apiObtenerValePorCambio.replace("{folio}", encodeURIComponent(folio)),
        loader: "Consultando vale por cambio...",
      });
      if (response?.hasError) {
        dispatch({ type: 'SET_FIELD', field: 'remisionDevolucion', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        dispatch({ type: 'SET_FIELD', field: 'montoValePorCambio', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        const motivo = response?.message || "Vale rechazado por el sistema.";
        setValeMensaje({ tipo: 'error', texto: `Vale ${folio} rechazado: ${motivo}` });
        writeInternalLog("error", `Vale ${folio} rechazado`, response);
        focusFolioVale();
        return;
      }
      const vale = response?.result ?? {};
      const remisionVale = vale.remisionorigen ?? vale.remisionOrigen ?? vale.remisiondevolucion ?? vale.remisionDevolucion ?? vale.remision ?? "";
      const montoVale = vale.monto ?? vale.montoVale ?? vale.montovale ?? vale.montoValePorCambio ?? vale.montovaleporcambio ?? "";
      const statusVale = String(vale.status ?? vale.estatus ?? vale.estado ?? "ACTIVO").trim().toUpperCase();
      const caducidadVale = vale.fechacaducidad ?? vale.fechaCaducidad ?? vale.caducidad ?? "";
      if (statusVale && statusVale !== "ACTIVO") {
        dispatch({ type: 'SET_FIELD', field: 'remisionDevolucion', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        dispatch({ type: 'SET_FIELD', field: 'montoValePorCambio', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        setValeMensaje({ tipo: 'error', texto: `Vale ${folio} rechazado: status ${statusVale}. No disponible para aplicar.` });
        focusFolioVale();
        return;
      }
      if (!String(remisionVale).trim() || !(parseAmount(montoVale) > 0)) {
        dispatch({ type: 'SET_FIELD', field: 'remisionDevolucion', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        dispatch({ type: 'SET_FIELD', field: 'montoValePorCambio', value: "", summaryTotal: summaryRef.current?.total ?? 0 });
        setValeMensaje({ tipo: 'error', texto: `Vale ${folio} rechazado: el sistema no devolvió remisión y monto vélidos.` });
        focusFolioVale();
        return;
      }
      const totalActual = Number(summaryRef.current?.total) || 0;
      dispatch({ type: 'SET_FIELD', field: 'remisionDevolucion', value: String(remisionVale).replace(/\D/g, ""), summaryTotal: totalActual });
      dispatch({ type: 'SET_FIELD', field: 'montoValePorCambio', value: String(montoVale), summaryTotal: totalActual });
      const detalleCaducidad = caducidadVale ? ` Vence: ${caducidadVale}.` : "";
      const cubreTotal = parseAmount(montoVale) >= totalActual && totalActual > 0;
      const detalleFiniquito = cubreTotal ? " Cubre el total: efectivo en 0, el vale se finiquita y el excedente se pierde." : "";
      setValeMensaje({ tipo: 'success', texto: `Vale ${folio} disponible. Remisión ${remisionVale}, monto ${NumberFormat$(parseAmount(montoVale))}.${detalleCaducidad}${detalleFiniquito}` });
      writeInternalLog("info", `Vale ${folio} cargado por código de barras`);
    } catch (error) {
      setValeMensaje({ tipo: 'error', texto: `Vale ${folio} rechazado: ${error?.message || "error al consultar el vale."}` });
      focusFolioVale();
    } finally {
      setConsultandoVale(false);
    }
  }, [GetRequest, focusFolioVale, writeInternalLog]);
  const handleChangeTipoPago = useCallback((tipoPago) => {
    if (tipoPago === "CRÉDITO A CLIENTE" && !tieneCliente) {
      setErrorMensaje(
        "No es posible generar un crédito a cliente: primero seleccione y cargue un cliente registrado en la venta."
      );
      writeInternalLog("error", "Intento de crédito a cliente sin cliente cargado", { objeto: tipoPago, tieneCliente });
      return;
    }
    dispatch({ type: 'CHANGE_PAYMENT_TYPE', tipoPago, total: summary.total });
    setErrorMensaje("");
    writeInternalLog("info", `Tipo de pago cambiado a ${tipoPago}`);
    focusInputByPaymentType(tipoPago);
  }, [writeInternalLog, focusInputByPaymentType, summary.total, tieneCliente]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const summaryRef = useRef(summary);
  summaryRef.current = summary;
  const CobrarVenta = useCallback(async () => {
    if (isProcessingRef.current) return;
    const currentState = stateRef.current;
    const currentSummary = summaryRef.current;
    if (currentState.tipoPago === "CRÉDITO A CLIENTE" && !tieneCliente) {
      setErrorMensaje(
        "No es posible generar un crédito a cliente: primero seleccione y cargue un cliente registrado en la venta."
      );
      return;
    }
    const validationError = validateBeforeCharge(currentState, currentSummary);
    if (validationError) {
      setErrorMensaje(validationError);
      writeInternalLog("error", validationError, { state: currentState, summary: currentSummary });
      return;
    }
    setErrorMensaje("");
    setIsProcessing(true);
    try {
      await handleCobrar(buildPayload(currentState, currentSummary));
      writeInternalLog("info", "Cobro ejecutado correctamente");
    } catch (error) {
      const message = error?.message || "Ocurrió un error al cobrar la venta.";
      setErrorMensaje(message);
      writeInternalLog("error", message, error);
    } finally {
      setIsProcessing(false);
    }
  }, [handleCobrar, writeInternalLog, tieneCliente]);
  useEffect(() => {
    let timerOpen;
    let timerVisible;
    if (openModalCobrarVentas) {
      setInternalOpen(true);
      // Al abrir fresco, el vale inicia desactivado: cobro normal como antes (no arrastra folio de otra venta)
      if (!prevCobroOpenRef.current) {
        dispatch({ type: 'SET_FIELD', field: 'aplicaValePorCambio', value: false, summaryTotal: summary.total });
      }
      handleChangeTipoPago("EFECTIVO");
      // dispatch({ type: 'RESET', tipoPago: montosVenta?.tipoPago || "EFECTIVO", total: summary.total });
      setErrorMensaje("");
      setValeMensaje(null);
      setConsultandoVale(false);
      setIsProcessing(false);
      logRef.current = [];
      writeInternalLog("info", "Modal abierto", { summary });
      timerOpen = setTimeout(() => {
        setModalVisible(true);
        focusInputByPaymentType(montosVenta?.tipoPago || "EFECTIVO");
      }, 50);
    } else {
      setModalVisible(false);
      timerVisible = setTimeout(() => {
        setInternalOpen(false);
      }, 350);
    }
    prevCobroOpenRef.current = openModalCobrarVentas;
    return () => {
      if (timerOpen) clearTimeout(timerOpen);
      if (timerVisible) clearTimeout(timerVisible);
    };
  }, [openModalCobrarVentas, montosVenta?.tipoPago, summary, writeInternalLog, focusInputByPaymentType]);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (state.cambio <= 0) {
      dispatch({ type: 'SET_CAMBIO_LETRA', value: 'CERO PESOS' });
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const letra = await MontoEnLetra(state.cambio);
        if (mountedRef.current) {
          dispatch({ type: 'SET_CAMBIO_LETRA', value: letra || 'CERO PESOS' });
        }
      } catch {
        if (mountedRef.current) {
          dispatch({ type: 'SET_CAMBIO_LETRA', value: 'CERO PESOS' });
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [state.cambio]);
  useEffect(() => {
    if (!openModalCobrarVentas) return;
    const onKeyDown = (event) => {
      if (isProcessingRef.current) return;
      if (event.altKey && (event.key === "End" || event.code === "End")) {
        event.preventDefault();
        if (canChargeRef.current) {
          CobrarVenta();
        }
        return;
      }
      if (event.altKey && event.code === "KeyE") {
        event.preventDefault();
        handleChangeTipoPago("EFECTIVO");
        return;
      }
      if (event.altKey && event.code === "KeyC") {
        event.preventDefault();
        handleChangeTipoPago("TARJETA CRÉDITO");
        return;
      }
      if (event.altKey && event.code === "KeyD") {
        event.preventDefault();
        handleChangeTipoPago("TARJETA DÉBITO");
        return;
      }
      if (event.altKey && event.code === "KeyT") {
        event.preventDefault();
        handleChangeTipoPago("TRANSFERENCIA");
        return;
      }
      if (event.altKey && event.code === "KeyV") {
        event.preventDefault();
        event.stopPropagation();
        handleChangeTipoPago("VALES");
        return;
      }
      if (event.altKey && event.code === "F6") {
        event.preventDefault();
        handleChangeTipoPago("CRÉDITO A CLIENTE");
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [openModalCobrarVentas, CobrarVenta, handleChangeTipoPago]);
  return (
    <Modal
      open={internalOpen}
      onClose={isProcessing ? undefined : handleCloseModalCobroVentas}
      closeAfterTransition
      disableEnforceFocus
    >
      <Box
        sx={{
          ...modalStyle,
          transform: modalVisible ? "translateX(0)" : "translateX(300%)",
        }}
      >
        <Fragment>
          {errorMensaje ? (
            <Alert severity="error" sx={{ mb: 1, position: "sticky", top: 0, zIndex: 3, boxShadow: 2 }} onClose={() => setErrorMensaje("")}>
              {errorMensaje}
            </Alert>
          ) : null}
          <div className="containerCobroVentasMenu">
            <Tooltip title="Pago en efectivo">
              <IconButton onClick={() => handleChangeTipoPago("EFECTIVO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AttachMoney sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+E
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Pago con tarjeta crédito">
              <IconButton onClick={() => handleChangeTipoPago("TARJETA CRÉDITO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <CreditCard sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+C
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Pago con tarjeta débito">
              <IconButton onClick={() => handleChangeTipoPago("TARJETA DÉBITO")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <CreditCard sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+D
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Pago mediante transferencia">
              <IconButton onClick={() => handleChangeTipoPago("TRANSFERENCIA")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AccountBalance sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+T
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Pago con vales">
              <IconButton onClick={() => handleChangeTipoPago("VALES")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AccountBalanceWallet sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+V
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Pago a crédito">
              <IconButton onClick={() => handleChangeTipoPago("CRÉDITO A CLIENTE")} color="primary">
                <Box sx={{ position: "relative" }}>
                  <AccountBalance sx={{ fontSize: 60 }} />
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 33,
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "2px 6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                      transform: "translate(50%, 50%)",
                    }}
                  >
                    Alt+F6
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
          </div>
          <div className="venta-detalles-container" style={{ textAlign: "center", marginTop: 0, paddingTop: 0 }}>
            <div className="detalle-item" style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "center", gap: "0 1rem" }}>
              <span><span className="detalle-text">Descuento:</span> {NumberFormat$(summary.totalDescuento)}</span>
              <span><span className="detalle-text">Subtotal:</span> {NumberFormat$(summary.subTotal)}</span>
              <span><span className="detalle-text">IVA:</span> {NumberFormat$(summary.totalIVA)}</span>
            </div>
            <h1 style={{ margin: "0.1rem 0", lineHeight: 1.1 }}>{NumberFormat$(summary.total)}</h1>
            {state.aplicaValePorCambio && state.montoValePorCambioNum > 0 && (
              <div style={{ margin: "0.25rem 0" }}>
                <span className="detalle-text">Vale {state.folioValePorCambio || ""}:</span> {NumberFormat$(state.montoValePorCambioNum)}
                <span className="detalle-text"> + Efectivo:</span> {NumberFormat$(state.montoEfectivoNum)}
                {requiresMontoOtro && (<><span className="detalle-text"> + Otro:</span> {NumberFormat$(state.montoOtroNum)}</>)}
                <span className="detalle-text"> = A cubrir:</span> {NumberFormat$(totalACubrir)}
              </div>
            )}
            <div>
              <span className="detalle-text">Monto en letra:</span> {summary.montoEnLetra}
            </div>
          </div>
          <div className="containerCobroVentas">
            <div className="actionSectionCobroVenta">
              <TextField autoComplete="off"
                select
                inputRef={refTipoPago}
                label="Tipo de pago"
                value={state.tipoPago}
                onChange={(e) => handleChangeTipoPago(String(e.target.value))}
                sx={{ margin: ".5rem .3rem", width: "250px" }}
              >
                {PAYMENT_TYPES.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </TextField>
              {isCredito && (
                <TextField autoComplete="off"
                  select
                  label="Forma de pago del abono inicial"
                  value={state.formaPagoAbonoInicial}
                  onChange={(e) => handleInputChange("formaPagoAbonoInicial", String(e.target.value))}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                >
                  {PAYMENT_TYPES.filter((x) => x !== "CRÉDITO A CLIENTE").map((x) => (
                    <MenuItem key={x} value={x}>{x}</MenuItem>
                  ))}
                </TextField>
              )}
              {requiresReferenciaTarjeta && (
                <TextField autoComplete="off"
                  inputRef={refDigitos}
                  label="4 dígitos tarjeta"
                  value={state.digitosTarjeta}
                  onChange={(e) => dispatch({ type: 'SET_DIGITOS_TARJETA', value: e.target.value })}
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
              {requiresReferenciaTransferencia && (
                <TextField autoComplete="off"
                  inputRef={refTransferencia}
                  label="Datos transferencia"
                  value={state.datosTransferencia}
                  onChange={(e) => handleInputChange("datosTransferencia", e.target.value)}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
              {requiresReferenciaCredito && (
                <TextField autoComplete="off"
                  inputRef={refCreditoCliente}
                  label="Datos crédito a cliente"
                  value={state.datosCreditoCliente}
                  onChange={(e) => handleInputChange("datosCreditoCliente", e.target.value)}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
              {requiresReferenciaVales && (
                <TextField autoComplete="off"
                  inputRef={refVales}
                  label="Folio / referencia vale"
                  value={state.datosVales}
                  onChange={(e) => handleInputChange("datosVales", e.target.value)}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
              <Box sx={{ width: "100%", maxWidth: 520, mx: "auto", my: 1, px: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.aplicaValePorCambio}
                      onChange={(e) => handleToggleAplicaVale(e.target.checked)}
                    />
                  }
                  label="Aplicar vale por cambio"
                />
                {state.aplicaValePorCambio && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1 }}>
                    <TextField autoComplete="off"
                      inputRef={refFolioValePorCambio}
                      label="Folio vale (escanee + Enter)"
                      value={state.folioValePorCambio}
                      onChange={(e) => handleFolioValeChange(e.target.value)}
                      onFocus={(e) => e.target.select?.()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          consultarValePorFolio(e.target.value);
                        }
                      }}
                      inputProps={{ inputMode: "numeric" }}
                      sx={{ width: "190px" }}
                    />
                    <TextField autoComplete="off"
                      label="Remision origen"
                      value={state.remisionDevolucion}
                      InputProps={{ readOnly: true }}
                      inputProps={{ inputMode: "numeric", tabIndex: -1 }}
                      sx={{ width: "170px", "& .MuiInputBase-input.Mui-readOnly": { backgroundColor: "#f5f5f5" } }}
                    />
                    <TextField autoComplete="off"
                      label="Monto vale"
                      value={state.montoValePorCambio}
                      InputProps={{ readOnly: true }}
                      inputProps={{ inputMode: "decimal", tabIndex: -1 }}
                      sx={{ width: "150px", "& .MuiInputBase-input.Mui-readOnly": { backgroundColor: "#f5f5f5" } }}
                    />
                  </Box>
                )}
                {state.aplicaValePorCambio && valeMensaje?.texto && (
                  <Alert severity={valeMensaje.tipo === "error" ? "error" : valeMensaje.tipo === "warning" ? "warning" : valeMensaje.tipo === "success" ? "success" : "info"} sx={{ mt: 1 }}>
                    {consultandoVale ? "Consultando vale..." : valeMensaje.texto}
                  </Alert>
                )}
              </Box>
              {isCredito && (
                <TextField autoComplete="off"
                  label="Saldo crédito a cliente"
                  value={formatLiquidacion(montoCreditoCliente)}
                  type="text"
                  InputProps={{ readOnly: true }}
                  inputProps={{ inputMode: "decimal", tabIndex: -1 }}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
              {((!isCredito && requiresMontoOtro) || mostrarMontoTipoPagoAbonoInicial) && (
                <TextField autoComplete="off"
                  inputRef={refMontoOtro}
                  label={isCredito
                    ? `Monto ${state.formaPagoAbonoInicial} del abono inicial`
                    : "Monto tipo pago"}
                  value={state.montoOtro}
                  onChange={(e) => handleInputChange("montoOtro", e.target.value)}
                  type="text"
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ margin: ".5rem .3rem", width: "250px" }}
                />
              )}
            </div>
            <div className="actionSectionCobroVenta" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <TextField autoComplete="off"
                inputRef={refMontoEfectivo}
                label={isCredito
                  ? "Monto abono inicial total"
                  : totalACubrir === 0 && state.aplicaValePorCambio
                    ? "Monto efectivo (cubierto por vale)"
                    : "Monto efectivo"}
                value={isCredito ? formatLiquidacion(montoAbonoInicial) : state.montoEfectivo}
                onChange={isCredito ? undefined : (e) => handleInputChange("montoEfectivo", e.target.value)}
                type="text"
                disabled={!isCredito && totalACubrir === 0 && state.aplicaValePorCambio}
                InputProps={isCredito ? { readOnly: true } : undefined}
                inputProps={{ inputMode: "decimal", tabIndex: isCredito ? -1 : undefined }}
                sx={{ margin: ".5rem .3rem", width: "460px" }}
              />
              {isCredito && (
                <TextField autoComplete="off"
                  inputRef={refMontoEfectivo}
                  label="Efectivo del abono inicial"
                  value={state.montoEfectivo}
                  onChange={(e) => handleInputChange("montoEfectivo", e.target.value)}
                  type="text"
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ margin: ".5rem .3rem", width: "460px" }}
                />
              )}
              <hr className="separator" style={{ width: "460px" }} />
              <TextField autoComplete="off"
                label="Monto cambio"
                value={`${NumberFormat$(state.cambio)}\n(${state.montoCambioLetra})`}
                multiline
                disabled
                sx={{
                  width: "460px",
                  margin: ".5rem .3rem",
                  backgroundColor: state.faltante > 0 ? "#ff3333" : "white",
                  "& .MuiInputBase-input": {
                    color: state.faltante > 0 ? "white" : "black",
                    fontWeight: "bold",
                  },
                }}
              />
              <Box
                sx={{
                  width: "460px",
                  margin: ".25rem .3rem 0",
                  px: 1.5,
                  py: 0.75,
                  border: "1px solid",
                  borderColor: state.faltante > 0 ? "warning.light" : "success.light",
                  borderRadius: 1,
                  bgcolor: state.faltante > 0 ? "#fff8e1" : "#edf7ed",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 700,
                    color: state.faltante > 0 ? "warning.dark" : "success.dark",
                  }}
                >
                  <span>{state.faltante > 0 ? "Falta para completar venta" : "Venta cubierta"}</span>
                  <span>{NumberFormat$(state.faltante)}</span>
                </Typography>
              </Box>
              <div style={{ display: "flex", justifyContent: "center", margin: ".5rem .3rem" }}>
                <Button
                  variant="contained"
                  onClick={CobrarVenta}
                  disabled={!canCharge}
                  sx={{ height: 56, minWidth: 180 }}
                >
                  {isProcessing ? "Cobrando..." : "Cobrar venta"}
                </Button>
              </div>
            </div>
          </div>
        </Fragment>
      </Box>
    </Modal>
  );
}
